import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import Modal from 'react-modal';
import { useAccount, useWalletClient, useSwitchChain } from 'wagmi';
import { decodeEventLog, parseEther } from 'viem';
import { supabase, STORAGE_BUCKETS, uploadBase64ImageToStorage } from '../../../utils/supabase';
import { mainnet } from 'viem/chains';
import { monad } from '../../../utils/chains';
import { ink } from '../../../utils/chains';

// Chain configurations
const CHAINS = {
  ink: {
    id: 57073,
    name: 'Ink',
    color: '#9535fc',
    table: 'tokens',
    bucket: STORAGE_BUCKETS.LOGOS,
    factoryAddress: '0xE01118dbcF8Ba2609d3ffBD6A53d9533c7526D60', // V3 Factory (self-custody LP)
    npmAddress: '0xC0836E5B058BBE22ae2266e1AC488A1A0fD8DCE8', // Protofire
  },
  ethereum: {
    id: 1,
    name: 'Ethereum',
    color: '#627EEA',
    table: 'tokens_eth',
    bucket: STORAGE_BUCKETS.ETH_LOGOS,
    factoryAddress: '0x1D2c499996517b958fF715C8FBCcC631B2fDCB9B',
  },
  monad: {
    id: 143,
    name: 'Monad',
    color: '#836EF9',
    table: 'tokens_monad',
    bucket: STORAGE_BUCKETS.MONAD_LOGOS,
    factoryAddress: '0x09C38615b296023BfC1e6c55f1173d2398047807',
    npmAddress: '0x7197E214c0b767cFB76Fb734ab638E2c192F4E53',
  },
} as const;

type ChainKey = keyof typeof CHAINS;

// Factory ABIs
const INK_FACTORY_ABI = [
  {
    inputs: [
      { internalType: 'string', name: '_name', type: 'string' },
      { internalType: 'string', name: '_symbol', type: 'string' },
      { internalType: 'address', name: 'npm', type: 'address' }
    ],
    name: 'newMavrkToken',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'tokenAddress', type: 'address' },
      { indexed: false, internalType: 'string', name: 'name', type: 'string' },
      { indexed: false, internalType: 'string', name: 'symbol', type: 'string' },
      { indexed: true, internalType: 'address', name: 'creator', type: 'address' },
      { indexed: true, internalType: 'address', name: 'npm', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'totalDeployed', type: 'uint256' }
    ],
    name: 'TokenDeployed',
    type: 'event'
  }
] as const;

const ETH_FACTORY_ABI = [
  {
    inputs: [
      { internalType: 'string', name: '_name', type: 'string' },
      { internalType: 'string', name: '_symbol', type: 'string' }
    ],
    name: 'newMavrkToken',
    outputs: [],
    stateMutability: 'nonpayable',
    type: 'function'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'tokenAddress', type: 'address' },
      { indexed: false, internalType: 'string', name: 'name', type: 'string' },
      { indexed: false, internalType: 'string', name: 'symbol', type: 'string' },
      { indexed: true, internalType: 'address', name: 'creator', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'totalDeployed', type: 'uint256' }
    ],
    name: 'TokenDeployed',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: 'address', name: 'pool', type: 'address' }
    ],
    name: 'PoolInitialized',
    type: 'event'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: false, internalType: 'uint256', name: 'tokenId', type: 'uint256' },
      { indexed: true, internalType: 'address', name: 'creator', type: 'address' }
    ],
    name: 'LiquidityMinted',
    type: 'event'
  }
] as const;

const MONAD_FACTORY_ABI = [
  {
    inputs: [
      { internalType: 'string', name: '_name', type: 'string' },
      { internalType: 'string', name: '_symbol', type: 'string' },
      { internalType: 'address', name: 'npm', type: 'address' }
    ],
    name: 'newMavrkToken',
    outputs: [],
    stateMutability: 'payable',
    type: 'function'
  },
  {
    anonymous: false,
    inputs: [
      { indexed: true, internalType: 'address', name: 'tokenAddress', type: 'address' },
      { indexed: false, internalType: 'string', name: 'name', type: 'string' },
      { indexed: false, internalType: 'string', name: 'symbol', type: 'string' },
      { indexed: true, internalType: 'address', name: 'creator', type: 'address' },
      { indexed: true, internalType: 'address', name: 'npm', type: 'address' },
      { indexed: false, internalType: 'uint256', name: 'totalDeployed', type: 'uint256' }
    ],
    name: 'TokenDeployed',
    type: 'event'
  }
] as const;

interface UnifiedCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

// Styled Components
const ModalContent = styled.div`
  background: #000000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  width: 500px;
  max-height: 680px;
  overflow-y: auto;
  padding: 32px;
  box-sizing: border-box;
  
  @media (max-width: 540px) {
    width: 95vw;
    max-height: 90vh;
    padding: 24px 16px;
    border-radius: 12px;
  }
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  margin: 0 0 8px 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
  margin: 0 0 24px 0;
  text-align: center;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 600;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  margin-bottom: 8px;
  
  .required {
    color: #ff4444;
  }
`;

const Input = styled.input`
  width: 100%;
  background: #000000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 12px 16px;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;
  outline: none;
  transition: all 0.2s ease;

  &:focus {
    background: #252525;
    border-color: #9535fc;
    box-shadow: 0 0 0 3px rgba(149, 53, 252, 0.15);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
  
  &:disabled {
    background: #000000;
    cursor: not-allowed;
  }
`;

const Select = styled.select<{ $color?: string }>`
  width: 100%;
  background: #000000;
  border: 2px solid ${props => props.$color || 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  padding: 12px 16px;
  color: #FFFFFF;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  box-sizing: border-box;
  outline: none;
  cursor: pointer;
  transition: all 0.2s ease;
  appearance: none;
  background-image: url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fff' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 16px;

  &:focus {
    background-color: #252525;
    border-color: ${props => props.$color || '#9535fc'};
    box-shadow: 0 0 0 3px ${props => props.$color ? `${props.$color}30` : 'rgba(149, 53, 252, 0.15)'};
  }
  
  &:disabled {
    background-color: #000000;
    cursor: not-allowed;
  }
  
  option {
    background: #000000;
    color: #FFFFFF;
  }
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  min-height: 120px;
  background: #000000;
  border: 2px dashed rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  padding: 20px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  box-sizing: border-box;

  &:hover {
    background: #252525;
    border-color: #9535fc;
    color: #9535fc;
  }
`;

const LogoPreview = styled.img`
  width: 80px;
  height: 80px;
  border-radius: 12px;
  object-fit: cover;
  border: 2px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 8px;
`;

const Button = styled.button<{ disabled?: boolean; $color?: string }>`
  width: 100%;
  padding: 14px 24px;
  background: ${props => props.disabled ? '#CCCCCC' : (props.$color || '#9535fc')};
  border: none;
  border-radius: 10px;
  color: #FFFFFF;
  font-size: 16px;
  font-weight: 600;
  font-family: 'Inter', sans-serif;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  margin-top: 8px;
  box-shadow: ${props => props.disabled ? 'none' : `0 4px 12px ${props.$color || '#9535fc'}40`};

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px ${props => props.$color || '#9535fc'}50;
    opacity: 0.9;
  }
`;

const StatusMessage = styled.div<{ type: 'info' | 'success' | 'error' }>`
  padding: 12px 16px;
  border-radius: 8px;
  margin-bottom: 20px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  text-align: center;
  background: ${props =>
    props.type === 'error' ? 'rgba(255, 68, 68, 0.1)' :
    props.type === 'success' ? 'rgba(16, 185, 129, 0.1)' :
    'rgba(149, 53, 252, 0.1)'};
  color: ${props =>
    props.type === 'error' ? '#ff4444' :
    props.type === 'success' ? '#10b981' :
    '#9535fc'};
  border: 1px solid ${props =>
    props.type === 'error' ? 'rgba(255, 68, 68, 0.2)' :
    props.type === 'success' ? 'rgba(16, 185, 129, 0.2)' :
    'rgba(149, 53, 252, 0.2)'};
`;

const ChainInfo = styled.div<{ $color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 12px 14px;
  background: ${props => `${props.$color}15`};
  border: 1px solid ${props => `${props.$color}40`};
  border-radius: 8px;
  margin-bottom: 20px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.8);
  
  strong {
    color: ${props => props.$color};
  }
`;

const UnifiedCreateModal: React.FC<UnifiedCreateModalProps> = ({
  isOpen,
  onClose,
  onSuccess
}) => {
  const { address, isConnected } = useAccount();
  const { data: walletClient } = useWalletClient();
  const { switchChain } = useSwitchChain();

  const [selectedChain, setSelectedChain] = useState<ChainKey>('ink');
  const [formData, setFormData] = useState({ name: '', symbol: '' });
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string>('');
  const [status, setStatus] = useState<{ type: 'info' | 'success' | 'error'; message: string } | null>(null);
  const [isDeploying, setIsDeploying] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const chainConfig = CHAINS[selectedChain];

  // Helper to resize image
  const resizeImage = (file: File, maxSize: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > height) {
            if (width > maxSize) {
              height = (height * maxSize) / width;
              width = maxSize;
            }
          } else {
            if (height > maxSize) {
              width = (width * maxSize) / height;
              height = maxSize;
            }
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }
          ctx.drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setLogoFile(file);
      const preview = await resizeImage(file, 512);
      setLogoPreview(preview);
    }
  };

  const handleDeploy = async () => {
    if (!formData.name || !formData.symbol) {
      setStatus({ type: 'error', message: 'Name and symbol are required' });
      return;
    }

    if (!logoFile) {
      setStatus({ type: 'error', message: 'Logo is required' });
      return;
    }

    setIsDeploying(true);
    setStatus({ type: 'info', message: `Deploying on ${chainConfig.name}...` });

    try {
      const logoBase64 = await resizeImage(logoFile, 512);
      let deployedTokenAddress: string | null = null;
      let poolAddress: string | null = null;
      let nftTokenId: string | null = null;

      if (selectedChain === 'ink') {
        // Ink: Direct wallet deployment to Protofire DEX
        if (!isConnected || !address || !walletClient) {
          throw new Error('Please connect your wallet');
        }

        setStatus({ type: 'info', message: 'Switching to Ink...' });
        const currentChainId = await walletClient.getChainId();
        if (currentChainId !== chainConfig.id) {
          await switchChain({ chainId: chainConfig.id });
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        setStatus({ type: 'info', message: 'Deploying token contract...' });

        const hash = await walletClient.writeContract({
          address: chainConfig.factoryAddress as `0x${string}`,
          abi: INK_FACTORY_ABI,
          functionName: 'newMavrkToken',
          args: [formData.name, formData.symbol, CHAINS.ink.npmAddress],
        });

        setStatus({ type: 'info', message: 'Waiting for confirmation...' });

        const { createPublicClient, http } = await import('viem');
        const inkPublicClient = createPublicClient({
          chain: ink,
          transport: http()
        });

        const receipt = await inkPublicClient.waitForTransactionReceipt({ hash });

        if (receipt.status !== 'success') {
          throw new Error('Transaction failed');
        }

        // Parse logs
        for (const log of receipt.logs) {
          try {
            if (log.address?.toLowerCase() !== chainConfig.factoryAddress.toLowerCase()) continue;
            
            const decoded = decodeEventLog({
              abi: INK_FACTORY_ABI,
              data: log.data,
              topics: log.topics as [signature: `0x${string}`, ...args: `0x${string}`[]]
            });

            if (decoded.eventName === 'TokenDeployed' && decoded.args) {
              deployedTokenAddress = (decoded.args as any).tokenAddress;
            }
          } catch (e) {
            console.warn('Failed to decode log:', e);
          }
        }

      } else if (selectedChain === 'ethereum') {
        // Ethereum: Direct wallet deployment
        if (!isConnected || !address || !walletClient) {
          throw new Error('Please connect your wallet');
        }

        setStatus({ type: 'info', message: 'Switching to Ethereum...' });
        const currentChainId = await walletClient.getChainId();
        if (currentChainId !== chainConfig.id) {
          await switchChain({ chainId: chainConfig.id });
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        setStatus({ type: 'info', message: 'Deploying token contract...' });
        
        const hash = await walletClient.writeContract({
          address: chainConfig.factoryAddress as `0x${string}`,
          abi: ETH_FACTORY_ABI,
          functionName: 'newMavrkToken',
          args: [formData.name, formData.symbol],
        });

        setStatus({ type: 'info', message: 'Waiting for confirmation...' });

        const { createPublicClient, http } = await import('viem');
        const ethPublicClient = createPublicClient({
          chain: mainnet,
          transport: http()
        });

        const receipt = await ethPublicClient.waitForTransactionReceipt({ hash });

        if (receipt.status !== 'success') {
          throw new Error('Transaction failed');
        }

        // Parse logs
        for (const log of receipt.logs) {
          try {
            if (log.address?.toLowerCase() !== chainConfig.factoryAddress.toLowerCase()) continue;
            
            const decoded = decodeEventLog({
              abi: ETH_FACTORY_ABI,
              data: log.data,
              topics: log.topics as [signature: `0x${string}`, ...args: `0x${string}`[]]
            });

            if (decoded.eventName === 'TokenDeployed' && decoded.args) {
              deployedTokenAddress = (decoded.args as any).tokenAddress;
            } else if (decoded.eventName === 'PoolInitialized' && decoded.args) {
              poolAddress = (decoded.args as any).pool;
            } else if (decoded.eventName === 'LiquidityMinted' && decoded.args) {
              nftTokenId = (decoded.args as any).tokenId.toString();
            }
          } catch (e) {
            console.warn('Failed to decode log:', e);
          }
        }

      } else if (selectedChain === 'monad') {
        // Monad: Direct wallet deployment
        if (!isConnected || !address || !walletClient) {
          throw new Error('Please connect your wallet');
        }

        setStatus({ type: 'info', message: 'Switching to Monad...' });
        const currentChainId = await walletClient.getChainId();
        if (currentChainId !== chainConfig.id) {
          await switchChain({ chainId: chainConfig.id });
          await new Promise(resolve => setTimeout(resolve, 2000));
        }

        setStatus({ type: 'info', message: 'Deploying token contract...' });

        const hash = await walletClient.writeContract({
          address: chainConfig.factoryAddress as `0x${string}`,
          abi: MONAD_FACTORY_ABI,
          functionName: 'newMavrkToken',
          args: [formData.name, formData.symbol, CHAINS.monad.npmAddress],
          value: parseEther('0')
        });

        setStatus({ type: 'info', message: 'Waiting for confirmation...' });

        const { createPublicClient, http } = await import('viem');
        const monadPublicClient = createPublicClient({
          chain: monad,
          transport: http('https://rpc.monad.xyz')
        });

        const receipt = await monadPublicClient.waitForTransactionReceipt({ hash });

        if (receipt.status !== 'success') {
          throw new Error('Transaction failed');
        }

        // Parse logs
        for (const log of receipt.logs) {
          try {
            if (log.address?.toLowerCase() !== chainConfig.factoryAddress.toLowerCase()) continue;
            if (log.topics && log.topics.length === 4) {
              const tokenAddressTopic = log.topics[1];
              if (tokenAddressTopic && tokenAddressTopic.length === 66) {
                const tokenAddressHex = tokenAddressTopic.slice(26, 66);
                const potentialAddress = `0x${tokenAddressHex}`;
                if (/^0x[a-fA-F0-9]{40}$/.test(potentialAddress)) {
                  deployedTokenAddress = potentialAddress.toLowerCase();
                }
              }
            }
          } catch (e) {
            console.warn('Failed to parse log:', e);
          }
        }
      }

      if (!deployedTokenAddress) {
        throw new Error('Could not determine token address from transaction');
      }

      // Upload logo
      setStatus({ type: 'info', message: 'Uploading logo...' });
      const logoFileName = `${deployedTokenAddress}-logo.png`;
      const logoUrl = await uploadBase64ImageToStorage(
        logoBase64.split(',')[1],
        chainConfig.bucket,
        logoFileName
      );

      if (!logoUrl) {
        throw new Error('Failed to upload logo');
      }

      // Save to database
      setStatus({ type: 'info', message: 'Saving to database...' });
      
      const tokenData: any = {
        name: formData.name,
        symbol: formData.symbol,
        logo_url: logoUrl,
        contract_address: deployedTokenAddress,
        pool_address: poolAddress,
        nft_token_id: nftTokenId,
        deployer_address: address?.toLowerCase(),
      };

      if (selectedChain === 'ink') {
        tokenData.network = 'ink';
        tokenData.dex = 'Protofire';
      } else if (selectedChain === 'ethereum') {
        tokenData.network = 'eth';
      } else if (selectedChain === 'monad') {
        tokenData.network = 'monad';
      }

      const { error: insertError } = await supabase
        .from(chainConfig.table)
        .insert(tokenData);

      if (insertError) {
        throw new Error(`Failed to save token: ${insertError.message}`);
      }

      // Success!
      setStatus({
        type: 'success',
        message: `Token deployed on ${chainConfig.name}! ${deployedTokenAddress.slice(0, 6)}...${deployedTokenAddress.slice(-4)}`
      });

      // Reset form
      setFormData({ name: '', symbol: '' });
      setLogoFile(null);
      setLogoPreview('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Call success callback
      if (onSuccess) {
        setTimeout(onSuccess, 2000);
      }
      
      setTimeout(onClose, 2000);

    } catch (error: any) {
      console.error('Deployment error:', error);
      setStatus({
        type: 'error',
        message: error.message || 'Deployment failed. Please try again.'
      });
    } finally {
      setIsDeploying(false);
    }
  };

  // All chains require wallet connection
  const needsWallet = true;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Create Token"
      ariaHideApp={false}
      style={{
        overlay: {
          backgroundColor: 'rgba(0,0,0,0.85)',
          zIndex: 1000,
          backdropFilter: 'blur(8px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        },
        content: {
          maxWidth: '500px',
          margin: 'auto',
          borderRadius: '16px',
          background: 'transparent',
          border: 'none',
          padding: 0,
          inset: 'auto',
          position: 'relative',
          overflow: 'visible'
        },
      }}
    >
        <ModalContent>
          <Title>Create Token</Title>
          <Subtitle>Deploy across multiple chains with one click</Subtitle>

          {status && (
            <StatusMessage type={status.type}>{status.message}</StatusMessage>
          )}

          <FormGroup>
            <Label>Chain <span className="required">*</span></Label>
            <Select
              value={selectedChain}
              onChange={(e) => setSelectedChain(e.target.value as ChainKey)}
              disabled={isDeploying}
              $color={chainConfig.color}
            >
              <option value="ink">Ink Chain</option>
              <option value="ethereum">Ethereum</option>
              <option value="monad">Monad</option>
            </Select>
          </FormGroup>

          <ChainInfo $color={chainConfig.color}>
            {selectedChain === 'ink' ? (
              <>Deploys to <strong>Protofire DEX</strong></>
            ) : selectedChain === 'ethereum' ? (
              <>Deploys to <strong>Uniswap V3</strong></>
            ) : (
              <>Deploys to <strong>Monad DEX</strong></>
            )}
          </ChainInfo>

          <FormGroup>
            <Label>Token Name <span className="required">*</span></Label>
            <Input
              type="text"
              placeholder="My Token"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              disabled={isDeploying}
            />
          </FormGroup>

          <FormGroup>
            <Label>Token Symbol <span className="required">*</span></Label>
            <Input
              type="text"
              placeholder="TKN"
              value={formData.symbol}
              onChange={(e) => setFormData({ ...formData, symbol: e.target.value.toUpperCase() })}
              disabled={isDeploying}
              maxLength={10}
            />
          </FormGroup>

          <FormGroup>
            <Label>Logo <span className="required">*</span></Label>
            <FileInput
              ref={fileInputRef}
              id="unified-logo-upload"
              type="file"
              accept="image/*"
              onChange={handleLogoChange}
              disabled={isDeploying}
            />
            <FileInputLabel htmlFor="unified-logo-upload" onClick={(e) => {
              if (!isDeploying) {
                fileInputRef.current?.click();
              }
              e.preventDefault();
            }}>
              {logoPreview ? (
                <>
                  <LogoPreview src={logoPreview} alt="Logo preview" />
                  <span>Click to change</span>
                </>
              ) : (
                <>
                  <span style={{ fontSize: '24px', marginBottom: '8px' }}>📷</span>
                  <span>Click to upload logo</span>
                </>
              )}
            </FileInputLabel>
          </FormGroup>

          <Button 
            onClick={handleDeploy} 
            disabled={isDeploying || (needsWallet && !isConnected)}
            $color={chainConfig.color}
          >
            {isDeploying ? 'Deploying...' : `Deploy on ${chainConfig.name}`}
          </Button>

          {needsWallet && !isConnected && (
            <StatusMessage type="error" style={{ marginTop: '12px' }}>
              Please connect your wallet to deploy on {chainConfig.name}
            </StatusMessage>
          )}
        </ModalContent>
    </Modal>
  );
};

export default UnifiedCreateModal;

